<template>
  <el-drawer
    v-model="visible"
    direction="rtl"
    append-to-body
    size="60%"
    :show-close="false"
    class="tpl-editor-drawer"
    @open="onOpen"
  >
    <template #header>
      <div class="drawer-header">
        <div class="drawer-title">{{ isEditMode ? '编辑策略模板' : '新建策略模板' }}</div>
        <el-icon class="drawer-close" @click="visible = false"><Close /></el-icon>
      </div>
    </template>

    <div class="tpl-editor">
      <!-- ① 基础信息 -->
      <section class="ed-block ed-block--info">
        <div class="ed-block__head">
          <span class="ed-step">1</span>
          <span class="ed-block__title">基础信息</span>
        </div>
        <div class="ed-form">
          <div class="ed-form__item">
            <label class="ed-label">模板名称<span class="req">*</span></label>
            <el-input v-model="form.template_name" maxlength="30" show-word-limit placeholder="请输入模板名称" />
          </div>
          <div class="ed-form__item">
            <label class="ed-label">备注说明</label>
            <el-input v-model="form.memo" maxlength="100" show-word-limit placeholder="选填，用于区分模板用途" />
          </div>
        </div>
      </section>

      <!-- ② 策略编辑 -->
      <section class="ed-block ed-block--main">
        <div class="ed-block__head">
          <span class="ed-step">2</span>
          <span class="ed-block__title">策略配置</span>
          <el-radio-group v-model="currentTab" class="ed-tabs">
            <el-radio-button label="work">工作日</el-radio-button>
            <el-radio-button label="rest">休息日</el-radio-button>
          </el-radio-group>
          <div class="ed-tools">
            <el-button class="ed-tool-btn" @click="copyStrategy"><el-icon><DocumentCopy /></el-icon>复制</el-button>
            <el-button class="ed-tool-btn" @click="pasteStrategy" :disabled="!editing"><el-icon><Document /></el-icon>粘贴</el-button>
            <el-button class="ed-tool-btn ed-tool-btn--danger" @click="clearStrategy" :disabled="!editing"><el-icon><Delete /></el-icon>清空</el-button>
          </div>
        </div>

        <div class="ed-table-wrap">
          <el-table :data="currentTableData" class="ed-table" height="100%">
            <el-table-column label="开始时间" min-width="120" align="center">
              <template #default="{ row }">
                <input type="time" v-model="row.start_time" :disabled="!editing" class="ed-time">
              </template>
            </el-table-column>
            <el-table-column label="结束时间" min-width="120" align="center">
              <template #default="{ row }">
                <input type="time" v-model="row.end_time" :disabled="!editing" class="ed-time">
              </template>
            </el-table-column>
            <el-table-column label="功率 (kW)" min-width="110" align="center">
              <template #default="{ row }">
                <el-input-number v-model="row.power" :disabled="!editing" :min="-500" :max="500" :controls="false" class="ed-num" />
              </template>
            </el-table-column>
            <el-table-column label="上限 (%)" min-width="100" align="center">
              <template #default="{ row }">
                <el-input-number v-model="row.upper_limit" :disabled="!editing" :min="0" :max="100" :controls="false" class="ed-num" />
              </template>
            </el-table-column>
            <el-table-column label="下限 (%)" min-width="100" align="center">
              <template #default="{ row }">
                <el-input-number v-model="row.lower_limit" :disabled="!editing" :min="0" :max="100" :controls="false" class="ed-num" />
              </template>
            </el-table-column>
            <el-table-column label="运行模式" min-width="160" align="center">
              <template #default="{ row }">
                <el-select v-model="row.running_mode" :disabled="!editing" class="ed-mode">
                  <el-option v-for="opt in STRATEGY_MODE_OPTIONS" :key="opt.value" :label="opt.label" :value="opt.value" />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="130" align="center" fixed="right">
              <template #default="{ $index }">
                <el-button link type="primary" :disabled="!editing" @click="insertRow($index)">插入</el-button>
                <el-button link type="danger" :disabled="!editing" @click="deleteRow($index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <button class="ed-add" :disabled="!editing" @click="addRow">
            <el-icon><Plus /></el-icon>新增一行策略
          </button>
        </div>
      </section>

      <!-- ③ 策略预览 -->
      <section class="ed-block ed-block--chart-tab">
        <div class="ed-block__head">
          <span class="ed-step">3</span>
          <span class="ed-block__title">策略预览</span>
          <span class="ed-block__sub">{{ currentTab === 'work' ? '工作日' : '休息日' }}功率曲线</span>
        </div>
        <VChart class="ed-chart" :option="chartOption" autoresize />
      </section>
    </div>

    <template #footer>
      <div class="drawer-footer">
        <el-button @click="visible = false" size="default">取消</el-button>
        <el-button type="primary" :loading="saving" @click="onSave" size="default">保存模板</el-button>
      </div>
    </template>
  </el-drawer>
</template>

<script setup>
import { ref, reactive, computed, nextTick } from 'vue'
import VChart from 'vue-echarts'
import { Close, DocumentCopy, Document, Delete, Plus } from '@element-plus/icons-vue'
import { createTemplate, updateTemplate } from '../api.js'
import { createOption, isTimeSlotValid } from '../utils.js'

const STRATEGY_MODE_OPTIONS = [
  { label: '削峰填谷模式', value: 0 },
  { label: '光伏自发自用', value: 1 },
  { label: '只充光伏余电', value: 2 },
]

const emit = defineEmits(['saved'])

const visible = ref(false)
const editing = ref(true)
const saving = ref(false)
const currentTab = ref('work')
const tableRef = ref(null)

const form = reactive({
  id: null,
  template_name: '',
  memo: '',
  strategys_work: [],
  strategys_rest: [],
})

const isEditMode = computed(() => !!form.id)

const currentTableData = computed(() =>
  currentTab.value === 'work' ? form.strategys_work : form.strategys_rest
)

const chartOption = computed(() => {
  const data = currentTableData.value
  const chartData = (data || []).map(s => ({
    startTime: s.start_time,
    endTime: s.end_time,
    power: Number(s.power) || 0,
    runningMode: s.running_mode,
  }))
  const opt = createOption(chartData)
  opt.tooltip = {
    ...opt.tooltip,
    backgroundColor: '#FFFFFFF5',
    borderColor: 'rgba(0, 0, 0, 0.08)',
    borderWidth: 1,
    confine: true,
    extraCssText: 'z-index: 99999; border-radius: 8px; padding: 4px 10px; box-shadow: 0 6px 20px rgba(0, 0, 0, 0.14);',
  }
  return opt
})

function createEmptyStrategy(startTime = '00:00') {
  return {
    start_time: startTime,
    end_time: nextHour(startTime),
    power: 0,
    upper_limit: 100,
    lower_limit: 0,
    running_mode: 0,
    threshold_val: 10,
  }
}

/** 结束时间默认取开始时间 +1 小时（最大 24:00） */
function nextHour(time) {
  const [h, m] = String(time || '00:00').split(':').map(Number)
  const total = h * 60 + m + 60
  if (total >= 1440) return '24:00'
  const nh = String(Math.floor(total / 60)).padStart(2, '0')
  const nm = String(total % 60).padStart(2, '0')
  return `${nh}:${nm}`
}

function open(template) {
  if (template) {
    form.id = template.id
    form.template_name = template.template_name || ''
    form.memo = template.memo || ''
    form.strategys_work = JSON.parse(JSON.stringify(template.strategys_work || []))
    form.strategys_rest = JSON.parse(JSON.stringify(template.strategys_rest || []))

    // 归一化 running_mode 为数字
    const normalize = (list) => list.map(s => ({ ...s, running_mode: Number(s.running_mode) || 0 }))
    form.strategys_work = normalize(form.strategys_work)
    form.strategys_rest = normalize(form.strategys_rest)
  } else {
    form.id = null
    form.template_name = ''
    form.memo = ''
    form.strategys_work = [createEmptyStrategy()]
    form.strategys_rest = [createEmptyStrategy()]
  }
  currentTab.value = 'work'
  visible.value = true
}

function onOpen() {
  nextTick(() => {
    editing.value = true
  })
}

function startEdit() {
  editing.value = true
}

function copyStrategy() {
  const data = currentTableData.value
  const text = JSON.stringify(data, null, 2)
  navigator.clipboard.writeText(text).then(() => {
    window.dispatchNotify({ type: 'success', message: '策略已复制到剪贴板' })
  })
}

function pasteStrategy() {
  navigator.clipboard.readText().then(text => {
    try {
      const data = JSON.parse(text)
      if (Array.isArray(data)) {
        if (currentTab.value === 'work') {
          form.strategys_work = data
        } else {
          form.strategys_rest = data
        }
        window.dispatchNotify({ type: 'success', message: '策略已粘贴' })
      } else {
        window.dispatchNotify({ type: 'error', message: '粘贴内容格式不正确' })
      }
    } catch (e) {
      window.dispatchNotify({ type: 'error', message: '粘贴内容格式不正确' })
    }
  })
}

function clearStrategy() {
  if (currentTab.value === 'work') {
    form.strategys_work = []
  } else {
    form.strategys_rest = []
  }
}

function addRow() {
  const target = currentTab.value === 'work' ? form.strategys_work : form.strategys_rest
  // 新增行的开始时间接续上一行的结束时间
  const last = target[target.length - 1]
  const startTime = last?.end_time && last.end_time !== '24:00' ? last.end_time : '00:00'
  target.push(createEmptyStrategy(startTime))
}

function insertRow(index) {
  const target = currentTab.value === 'work' ? form.strategys_work : form.strategys_rest
  // 插入行的开始时间接续当前行的结束时间
  const cur = target[index]
  const startTime = cur?.end_time && cur.end_time !== '24:00' ? cur.end_time : '00:00'
  target.splice(index + 1, 0, createEmptyStrategy(startTime))
}

function deleteRow(index) {
  const target = currentTab.value === 'work' ? form.strategys_work : form.strategys_rest
  if (target.length <= 1) {
    window.dispatchNotify({ type: 'warning', message: '至少保留一条策略' })
    return
  }
  target.splice(index, 1)
}

function validateStrategies() {
  const validate = (list, label) => {
    if (!list || !list.length) {
      window.dispatchNotify({ type: 'error', message: `${label}至少需要一条策略` })
      return false
    }
    for (let i = 0; i < list.length; i++) {
      const s = list[i]
      if (!s.start_time || !s.end_time) {
        window.dispatchNotify({ type: 'error', message: `${label}第${i+1}行时间未填写` })
        return false
      }
      if (s.power === '' || s.power === null || s.power === undefined) {
        window.dispatchNotify({ type: 'error', message: `${label}第${i+1}行功率未填写` })
        return false
      }
    }
    if (!isTimeSlotValid([...list])) {
      window.dispatchNotify({ type: 'error', message: `${label}存在时间段重叠或未覆盖全天（00:00～23:59），请检查后重试` })
      return false
    }
    return true
  }

  return validate(form.strategys_work, '工作日策略') && validate(form.strategys_rest, '休息日策略')
}

async function onSave() {
  if (!form.template_name.trim()) {
    window.dispatchNotify({ type: 'error', message: '请输入模板名称' })
    return
  }

  if (!validateStrategies()) return

  const numericStrategy = (list) => list.map((s) => ({
    ...s,
    power: Number(s.power) || 0,
    upper_limit: Number(s.upper_limit) || 0,
    lower_limit: Number(s.lower_limit) || 0,
    running_mode: Number(s.running_mode) || 0,
  }))

  const payload = {
    template_name: form.template_name.trim(),
    memo: form.memo || '',
    strategy_mode: '0',
    strategys_work: numericStrategy(form.strategys_work),
    strategys_rest: numericStrategy(form.strategys_rest),
  }
  if (form.id) payload.id = form.id

  saving.value = true
  try {
    const res = form.id ? await updateTemplate(payload) : await createTemplate(payload)
    if (res?.code === 200) {
      window.dispatchNotify({ type: 'success', message: form.id ? '模板修改成功' : '模板保存成功' })
      visible.value = false
      emit('saved')
    } else {
      window.dispatchNotify({ type: 'error', message: res?.msg || '保存失败' })
    }
  } finally {
    saving.value = false
  }
}

defineExpose({ open })
</script>

<style scoped lang="scss">
$primary: #3A68FF;

.tpl-editor-drawer {
 :deep(.el-drawer__header) {
    margin-bottom: 0 !important;
    padding: 0;
  }
  :deep(.el-drawer__body) {
    padding: 0;
    display: flex;
    flex-direction: column;
    background: #F4F7FC;
  }
  :deep(.el-drawer__footer) {
    padding: 12px 20px;
    background: #fff;
    border-top: 1px solid #EEF1F6;
  }
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #fff;
  border-bottom: 1px solid #EEF1F6;

  .drawer-title {
    font-size: 16px;
    font-weight: 600;
    color: #2e2e2e;
  }
  .drawer-close {
    font-size: 20px;
    color: rgba(46, 46, 46, 0.4);
    cursor: pointer;
    transition: color 0.2s;
    &:hover { color: $primary; }
  }
}

.tpl-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 14px 20px;
  gap: 12px;
  overflow: hidden;
  min-height: 0;
}

/* 通用区块卡片 */
.ed-block {
  background: #fff;
  border-radius: 14px;
  padding: 14px 18px;
  box-shadow: 0 2px 12px rgba(31, 45, 61, 0.04);

  &--main {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  &--info {
    padding-bottom: 12px;
    .ed-block__head { margin-bottom: 10px; }
  }
  &--chart {
    height: 200px;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
  }
}

.ed-block__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.ed-step {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: $primary;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ed-block__title {
  font-size: 15px;
  font-weight: 600;
  color: #2e2e2e;
}

.ed-block__sub {
  font-size: 13px;
  color: rgba(46, 46, 46, 0.45);
}

/* ① 基础信息 */
.ed-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  &__item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.ed-label {
  font-size: 14px;
  color: #2e2e2e;
  font-weight: 500;
  .req { color: #FF4D4F; margin-left: 3px; }
}

.ed-form ::v-deep .el-input__wrapper {
  border-radius: 10px;
  padding: 4px 14px;
}

/* ② 策略编辑 */
.ed-tabs {
  margin-left: 20px;
  ::v-deep .el-radio-button__inner {
    padding: 8px 20px;
    font-size: 14px;
    border-color: #DCE2EC;
  }
  ::v-deep .el-radio-button:first-child .el-radio-button__inner { border-radius: 8px 0 0 8px; }
  ::v-deep .el-radio-button:last-child .el-radio-button__inner { border-radius: 0 8px 8px 0; }
}

.ed-tools {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

.ed-tool-btn {
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #DCE2EC;
  background: #fff;
  color: #606266;
  font-size: 13px;

  .el-icon { margin-right: 4px; font-size: 14px; }

  &:hover:not(.is-disabled) {
    color: $primary;
    border-color: $primary;
    background: rgba(58, 104, 255, 0.06);
  }

  &--danger:hover:not(.is-disabled) {
    color: #FF4D4F;
    border-color: #FF4D4F;
    background: rgba(255, 77, 79, 0.06);
  }
}

.ed-table-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid #EEF1F6;
  border-radius: 12px;
  overflow: hidden;
}

.ed-table {
  flex: 1;

  ::v-deep .el-table__header th.el-table__cell {
    background: #F6F8FC;
    color: #5A6272;
    font-weight: 600;
    font-size: 13px;
    height: 46px;
    border-bottom: 1px solid #EEF1F6;
  }
  ::v-deep .el-table__body td.el-table__cell {
    padding: 8px 0;
    border-bottom: 1px solid #F4F6FA;
    .cell { padding: 0 8px; }
  }
  ::v-deep .el-table__row:hover td.el-table__cell {
    background: #F8FAFF;
  }
}

.ed-time {
  width: 100%;
  min-width: 100px;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #DCE2EC;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
  color: #2e2e2e;
  transition: border-color 0.2s;

  &:disabled { background: #F6F8FC; color: #A8B0BD; cursor: not-allowed; }
  &:focus:not(:disabled) { outline: none; border-color: $primary; }
}

.ed-num {
  width: 100%;
  ::v-deep .el-input__wrapper { border-radius: 8px; }
  ::v-deep .el-input__inner { text-align: center; font-size: 14px; }
}

.ed-mode {
  width: 100%;
  ::v-deep .el-input__wrapper { border-radius: 8px; }
  ::v-deep .el-input__inner { font-size: 14px; }
}

.ed-add {
  height: 44px;
  border: none;
  border-top: 1px dashed #DCE2EC;
  background: #FAFBFD;
  color: $primary;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.2s;

  &:hover:not(:disabled) { background: rgba(58, 104, 255, 0.06); }
  &:disabled { color: #A8B0BD; cursor: not-allowed; }
}

/* ③ 图表 */
.ed-chart {
  flex: 1;
  min-height: 0;
  width: 100%;
}
.ed-block--chart-tab{
  height: 250px;
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  ::v-deep .el-button {
    min-width: 90px;
    height: 38px;
    border-radius: 10px;
  }
}
</style>

<!-- drawer 被 append-to-body 传送到 body，scoped :deep 可能失效，用非 scoped 全局样式托底 -->
<style lang="scss">
.tpl-editor-drawer .el-drawer__header {
  margin-bottom: 0 !important;
  padding: 0;
}
.tpl-editor-drawer .drawer-footer .el-button--primary {
  color: #fff;
}
</style>
